import {Injectable, Injector} from '@angular/core';
import {RequestOptionsArgs} from '@angular/http';
import {Utilities} from '@shared/utilities/utilities.model';
import {SelectCommoditiesService} from '@shared/service/select-commodities.service';
import {
	CommonMessageCode,
	MessageCodeAndComponentType,
	MessageComponentType,
	MessageDetail,
	MessageType
} from '@shared/service/message-manager/message.model';
import {MessageManagerUtilities} from '@shared/service/message-manager/message-manager-utilities';
import {InnerMessageAuthorityMessageCode} from './inner-message-authority-utilities';
import {AuthorityBaseService} from './authority-base.service';
import {DeclineBody} from './quotation.service';

@Injectable({
	providedIn: 'root'
})
export class ConsipFlowService extends AuthorityBaseService {
	private innerAuthorityMessagesConfigurationMap: Map<ConsipFlowAction, MessageCodeAndComponentType> =
		new Map<ConsipFlowAction, MessageCodeAndComponentType>([
			[ConsipFlowAction.CONSIP_APPROVE, {messageCode: InnerMessageAuthorityMessageCode.AUTHORITY_SUCCESS_C_FLOW_APPROVE, componentType: MessageComponentType.FLASH_MESSAGE}],
			[ConsipFlowAction.CONSIP_EXTRA_FEE, {messageCode: InnerMessageAuthorityMessageCode.AUTHORITY_SUCCESS_GET_EXTRA_FEE, componentType: MessageComponentType.SNACK_BAR}],
			[ConsipFlowAction.REMOVE_EXTRA_FEE, {messageCode: InnerMessageAuthorityMessageCode.AUTHORITY_SUCCESS_REMOVED_EXTRA_FEE, componentType: MessageComponentType.SNACK_BAR}],
			[ConsipFlowAction.CONSIP_REJECT, {messageCode: InnerMessageAuthorityMessageCode.AUTHORITY_SUCCESS_C_FLOW_REJECT, componentType: MessageComponentType.FLASH_MESSAGE}],
			[ConsipFlowAction.SEND_TO_CONSIP, {messageCode: InnerMessageAuthorityMessageCode.AUTHORITY_SUCCESS_C_FLOW_SEND, componentType: MessageComponentType.FLASH_MESSAGE}]
		]);

	constructor(protected injector: Injector,
				private selectCommoditiesService: SelectCommoditiesService) {
		super(injector);
	}

	getConsipExtraTotal(dossierId: string, workingId: string, quotationId: string, entryIds: string[], isRemoving: boolean,
						onSuccess: (message: MessageDetail) => void, onFailure: (message: MessageDetail) => void): void {
		const url: string = `${this.environment.AUTHORITY}/dossiers/${dossierId}/workings/${workingId}/quotations/${quotationId}/entries/extra_fee`;
		const body: any = {
			entries_id: entryIds
		};

		const consipAction: ConsipFlowAction = isRemoving ? ConsipFlowAction.REMOVE_EXTRA_FEE : ConsipFlowAction.CONSIP_EXTRA_FEE;
		this.doPost(url, body, consipAction, onSuccess, onFailure);
	}

	sendToConsip(dossierId: string, workingId: string, quotationId: string, entryIds: string[],
				 onSuccess: (message: MessageDetail) => void, onFailure: (message: MessageDetail) => void): void {
		const url: string = `${this.environment.AUTHORITY}/dossiers/${dossierId}/workings/${workingId}/quotations/${quotationId}/entries/c_flow_send`;
		const body: any = {
			entries_id: entryIds
		};
		this.doPost(url, body, ConsipFlowAction.SEND_TO_CONSIP, onSuccess, onFailure);
	}

	consipApprove(dossierId: string, workingId: string, quotationId: string, onSuccess: (message: MessageDetail) => void, onFailure: (message: MessageDetail) => void, body?: ConsipApproveRequestBody): void {
		const url: string = `${this.environment.AUTHORITY}/dossiers/${dossierId}/workings/${workingId}/quotations/${quotationId}/c_flow_approve`;
		this.doPatch(url, onSuccess, onFailure, ConsipFlowAction.CONSIP_APPROVE, body);
	}

	consipReject(dossierId: string, workingId: string, quotationId: string, onSuccess: (message: MessageDetail) => void, onFailure: (message: MessageDetail) => void, declineBody?: DeclineBody): void {
		const url: string = `${this.environment.AUTHORITY}/dossiers/${dossierId}/workings/${workingId}/quotations/${quotationId}/c_flow_rejected`;
		this.doPatch(url, onSuccess, onFailure, ConsipFlowAction.CONSIP_REJECT, declineBody);
	}

	private doPatch(url: string, onSuccess: (message: MessageDetail) => void, onFailure: (message: MessageDetail) => void, type: ConsipFlowAction, body?: any): void {
		this.httpAuthService.patch(url, body, this.redirectUrl).subscribe(
			(response) => {
				if (response.ok) {
					onSuccess({
						type: MessageType.TYPE_SUCCESS,
						code: this.innerAuthorityMessagesConfigurationMap.get(type).messageCode,
						componentType: this.innerAuthorityMessagesConfigurationMap.get(type).componentType
					});
				} else {
					onFailure(MessageManagerUtilities.getMessageDetailsFromResponse(response));
				}
			}, (error) => {
				onFailure(MessageManagerUtilities.getMessageDetailsFromResponse(error));
			}
		);
	}

	private doPost(url: string, body: any, type: ConsipFlowAction, onSuccess: (message: MessageDetail) => void, onFailure: (message: MessageDetail) => void): void {
		const options: RequestOptionsArgs = Utilities.setOptionsWithNemoCommodityID(this.selectCommoditiesService.getIDCommodities());
		this.httpAuthService.post(url, body, this.redirectUrl, options).subscribe(
			response => {
				try {
					if (response.ok) {
						onSuccess({
							type: MessageType.TYPE_SUCCESS,
							code: this.innerAuthorityMessagesConfigurationMap.get(type).messageCode,
							componentType: this.innerAuthorityMessagesConfigurationMap.get(type).componentType
						});
					} else {
						onFailure(MessageManagerUtilities.getMessageDetailsFromResponse(response));
					}
				} catch (error) {
					onFailure({
						type: MessageType.TYPE_ERROR,
						code: CommonMessageCode.GENERIC_ERROR,
						componentType: MessageComponentType.FLASH_MESSAGE
					});
				}
			}, error => {
				onFailure(MessageManagerUtilities.getMessageDetailsFromResponse(error));
			}
		);
	}
}

export interface ConsipApproveRequestBody {
	oda_number: string;
	oda_date: string;
}

enum ConsipFlowAction {
	SEND_TO_CONSIP,
	CONSIP_REJECT,
	CONSIP_APPROVE,
	CONSIP_EXTRA_FEE,
	REMOVE_EXTRA_FEE
}
